import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useCurrency } from '../context/CurrencyContext';
import {
  Eye, EyeOff, User, Package, Heart, MapPin, LogOut,
  ChevronRight, Edit2, Mail, Shield, Loader2, ShoppingBag,
} from 'lucide-react';
import {
  login, register, logout, verifyOTP,
  saveToken, saveUser,
  getOrders, getWishlist, getAddresses,
  addAddress, deleteAddress, removeFromWishlist,
  updateProfile, changePassword, getUser,
  forgotPassword, resetPassword,  
} from '../api/auth';

/* ══ INPUT COMPONENT ══ */
const Input = ({ label, type = 'text', value, onChange, icon: Icon, extra, disabled }) => {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div className="ac-field">
      <label className="ac-label">{label}</label>
      <div className="ac-input-wrap">
        {Icon && <Icon size={15} className="ac-input-icon" />}
        <input
          className="ac-input"
          type={isPassword ? (show ? 'text' : 'password') : type}
          value={value}
          onChange={onChange}
          placeholder={label}
          disabled={disabled}
          style={{ paddingLeft: Icon ? '38px' : '14px' }}
        />
        {isPassword && (
          <button type="button" className="ac-eye" onClick={() => setShow(s => !s)}>
            {show ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
        )}
      </div>
      {extra}
    </div>
  );
};

/* ══ EMPTY STATE ══ */
const EmptyState = ({ icon: Icon, title, sub }) => (
  <div className="ac-empty">
    <div className="ac-empty-icon"><Icon size={28} strokeWidth={1.2} /></div>
    <p className="ac-empty-title">{title}</p>
    <p className="ac-empty-sub">{sub}</p>
  </div>
);

/* ══ SPINNER ══ */
const Spinner = () => (
  <div className="ac-spinner-wrap">
    <Loader2 size={22} className="ac-spinner" />
  </div>
);

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
const AccountPage = () => {
  const { formatPrice } = useCurrency();
  const [mode, setMode]         = useState('login'); // 'login' | 'register' | 'profile'
  const [tab, setTab]           = useState('orders');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [user, setUser]         = useState(null);
  const [otpMode, setOtpMode]   = useState(false);
  const [otp, setOtp]           = useState('');
  const [otpEmail, setOtpEmail] = useState('');

  // Tab data
  const [orders, setOrders]       = useState([]);
  const [wishlist, setWishlist]   = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [tabError, setTabError]   = useState('');

  // Edit profile modal
  const [editOpen, setEditOpen]     = useState(false);
  const [editName, setEditName]     = useState('');
  const [editPhone, setEditPhone]   = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');

  // Change password modal
  const [pwOpen, setPwOpen]     = useState(false);
  const [newPw, setNewPw]       = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg]       = useState('');

  // Add address modal
  // Fields match backend exactly: label, full_name, phone, line_1, line_2, city, state, pincode, is_default
  const [addrOpen, setAddrOpen]     = useState(false);
  const [addrSaving, setAddrSaving] = useState(false);
  const [addrError, setAddrError]   = useState('');
  const [addrForm, setAddrForm]     = useState({
    label: '', full_name: '', phone: '',
    line_1: '', line_2: '', city: '', state: '', pincode: '',  country: 'India', is_default: false,
  });

  const heroRef    = useRef(null);
  const heroInView = useInView(heroRef, { once: true });
  // Forgot password flow
  const [forgotMode, setForgotMode]       = useState(false); // 'email' | 'otp' | 'newpw'
  const [forgotStep, setForgotStep]       = useState('email');
  const [forgotEmail, setForgotEmail]     = useState('');
  const [forgotOtp, setForgotOtp]         = useState('');
  const [forgotNewPw, setForgotNewPw]     = useState('');
  const [forgotConfirmPw, setForgotConfirmPw] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError]     = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  /* ── Restore session on mount ── */
  useEffect(() => {
    const saved = getUser();
    if (saved) { setUser(saved); setMode('profile'); }
  }, []);

  /* ── Load tab data when tab or mode changes ── */
  useEffect(() => {
    if (mode !== 'profile') return;
    setTabError('');
    setTabLoading(true);

    const fetchers = {
      orders:   () => getOrders().then(setOrders),
      wishlist: () => getWishlist().then(setWishlist),
      address:  () => getAddresses().then(setAddresses),
      security: () => Promise.resolve(),
    };

    (fetchers[tab] || (() => Promise.resolve()))()
      .catch(err => setTabError(err.message))
      .finally(() => setTabLoading(false));
  }, [tab, mode]);

  /* ── Login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please fill in all fields.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const data = await login({ email, password });
      setUser(data.user);
      setMode('profile');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Register ── */
    const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { 
      setError('Please fill in all fields.'); return; 
    }
    if (password.length < 8) { 
      setError('Password must be at least 8 characters.'); return; 
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { 
      setError('Please enter a valid email address.'); return; 
    }
    setLoading(true); setError(''); setSuccess('');
    try {
      await register({ name, email, password });
      setOtpEmail(email);
      setOtpMode(true);
      setSuccess('OTP sent! Please check your email inbox.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleVerifyOTP = async (e) => {
  e.preventDefault();
  if (!otp || otp.length !== 6) { 
    setError('Please enter the 6-digit OTP.'); return; 
  }
  setLoading(true); setError(''); setSuccess('');
  try {
    const data = await verifyOTP({ email: otpEmail, otp });
    setMode('login');
    setOtpMode(false);
    setOtp('');
    setOtpEmail('');
    setName(''); setEmail(''); setPassword('');
    setSuccess('OTP verified. Please sign in with your new account.');
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};
  /* ── Logout ── */
  const handleLogout = async () => {
    await logout();
    setUser(null); setMode('login');
    setEmail(''); setPassword(''); setName('');
    setOrders([]); setWishlist([]); setAddresses([]);
  };

  /* ── Remove from wishlist ── */
  const handleRemoveWish = async (product_id) => {
    try {
      await removeFromWishlist(product_id);
      setWishlist(prev => prev.filter(w => w.id !== product_id));
    } catch (err) {
      console.error('Wishlist remove error:', err.message);
    }
  };

  /* ── Delete address ── */
  const handleDeleteAddress = async (id) => {
    try {
      await deleteAddress(id);
      setAddresses(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('Address delete error:', err.message);
    }
  };

  /* ── Open edit profile modal ── */
  const openEdit = () => {
    setEditName(user?.name || '');
    setEditPhone(user?.phone || '');
    setEditError('');
    setEditOpen(true);
  };

  /* ── Save profile ── */
  const handleSaveProfile = async () => {
    setEditSaving(true); setEditError('');
    try {
      const updated = await updateProfile({ name: editName, phone: editPhone });
      setUser(updated);
      setEditOpen(false);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  };


    /* ── Forgot password: send OTP ── */
  const handleForgotSendOtp = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { setForgotError('Please enter your email.'); return; }
    setForgotLoading(true); setForgotError(''); setForgotSuccess('');
    try {
      await forgotPassword(forgotEmail);
      setForgotSuccess('OTP sent! Check your inbox.');
      setForgotStep('otp');
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  
  /* ── Forgot password: verify OTP ── */
  const handleForgotVerifyOtp = async (e) => {
    e.preventDefault();
    if (!forgotOtp || forgotOtp.length !== 6) { setForgotError('Enter the 6-digit OTP.'); return; }
    setForgotLoading(true); setForgotError(''); setForgotSuccess('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-reset-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail, otp: forgotOtp }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setForgotStep('newpw');
      setForgotSuccess('OTP verified! Set your new password.');
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  /* ── Forgot password: reset password ── */
  const handleForgotReset = async (e) => {
    e.preventDefault();
    if (forgotNewPw.length < 8) { setForgotError('Password must be at least 8 characters.'); return; }
    if (forgotNewPw !== forgotConfirmPw) { setForgotError('Passwords do not match.'); return; }
    setForgotLoading(true); setForgotError(''); setForgotSuccess('');
    try {
      await resetPassword({ email: forgotEmail, otp: forgotOtp, new_password: forgotNewPw });
      setForgotSuccess('Password reset successfully! Please sign in.');
      setTimeout(() => {
        setForgotMode(false);
        setForgotStep('email');
        setForgotEmail(''); setForgotOtp('');
        setForgotNewPw(''); setForgotConfirmPw('');
        setForgotError(''); setForgotSuccess('');
      }, 2000);
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  /* ── Change password ── */
  const handleChangePassword = async () => {
    if (!newPw) return;
    if (newPw.length < 6) { setPwMsg('Password must be at least 6 characters.'); return; }
    setPwSaving(true); setPwMsg('');
    try {
      await changePassword(newPw);
      setPwMsg('Password updated successfully.');
      setNewPw('');
      setTimeout(() => { setPwOpen(false); setPwMsg(''); }, 1800);
    } catch (err) {
      setPwMsg(err.message);
    } finally {
      setPwSaving(false);
    }
  };

  /* ── Add address ── */
  const handleAddAddress = async () => {
    if (!addrForm.line_1 || !addrForm.city || !addrForm.pincode) {
      setAddrError('Line 1, city and pincode are required.'); return;
    }
    setAddrSaving(true); setAddrError('');
    try {
      const newAddr = await addAddress(addrForm);
      setAddresses(prev => [...prev, newAddr]);
      setAddrOpen(false);
      setAddrForm({ label:'', full_name:'', phone:'', line_1:'', line_2:'', city:'', state:'', pincode:'', country: 'India',  is_default:false });
    } catch (err) {
      setAddrError(err.message);
    } finally {
      setAddrSaving(false);
    }
  };

  /* ── Order status colour ── */
  const statusColor = (status = '') => {
      const s = status.toLowerCase();
      if (s === 'delivered') return { bg: '#2E7D3218', color: '#2E7D32' };
      if (s === 'shipped')   return { bg: '#1565C018', color: '#1565C0' };
      if (s === 'paid')      return { bg: '#2E7D3218', color: '#2E7D32' };
      if (s === 'cancelled') return { bg: '#C0392B18', color: '#C0392B' };
      return { bg: '#B08D5718', color: '#B08D57' };
    };

  const PROFILE_TABS = [
    { id: 'orders',   label: 'My Orders',  icon: Package },
    { id: 'wishlist', label: 'Wishlist',   icon: Heart   },
    { id: 'address',  label: 'Addresses',  icon: MapPin  },
    { id: 'security', label: 'Security',   icon: Shield  },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

        .ac-page {
          background:#FAF8F4; min-height:100vh;
          padding-top:162px; font-family:'Jost',sans-serif;
          position:relative; overflow-x:hidden;
        }
        .ac-page::before {
          content:''; position:fixed; inset:0; pointer-events:none; z-index:0;
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.022'/%3E%3C/svg%3E");
        }

        /* AUTH */
        .ac-auth-wrap {
          position:relative; z-index:1;
          display:grid; grid-template-columns:1fr 1fr;
          min-height:calc(100vh - 162px);
        }
        .ac-auth-left { position:relative; overflow:hidden; }
        .ac-auth-left img { width:100%; height:100%; object-fit:cover; display:block; }
        .ac-auth-left-overlay {
          position:absolute; inset:0;
          background:linear-gradient(135deg,rgba(12,9,5,0.78) 0%,rgba(28,20,8,0.55) 100%);
        }
        .ac-auth-left-content {
          position:absolute; inset:0; z-index:2;
          display:flex; flex-direction:column; justify-content:flex-end;
          padding:clamp(32px,5vw,60px);
        }
        .ac-auth-left-eyebrow {
          font-size:9px; font-weight:600; letter-spacing:0.3em;
          text-transform:uppercase; color:#B08D57;
          display:flex; align-items:center; gap:8px; margin-bottom:12px;
        }
        .ac-auth-left-eyebrow::before { content:''; width:20px; height:1px; background:#B08D57; }
        .ac-auth-left-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(28px,4vw,48px); font-weight:700;
          color:#FAF8F4; line-height:1.05; letter-spacing:-0.02em; margin-bottom:14px;
        }
        .ac-auth-left-title em { font-style:italic; font-weight:400; color:#C9A96E; display:block; }
        .ac-auth-left-sub {
          font-family:'Cormorant Garamond',serif;
          font-size:15px; font-style:italic; font-weight:300;
          color:rgba(250,248,244,0.6); line-height:1.7;
        }
        .ac-auth-right {
          display:flex; align-items:center; justify-content:center;
          padding:clamp(32px,5vw,64px); background:#FAF8F4;
        }
        .ac-form-box { width:100%; max-width:380px; }
        .ac-form-eyebrow {
          font-size:9px; font-weight:600; letter-spacing:0.28em;
          text-transform:uppercase; color:#B08D57;
          display:flex; align-items:center; gap:8px; margin-bottom:14px;
        }
        .ac-form-eyebrow::before { content:''; width:20px; height:1px; background:#B08D57; }
        .ac-form-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(26px,3.5vw,36px); font-weight:700;
          color:#1C1A18; line-height:1.05; letter-spacing:-0.02em; margin-bottom:6px;
        }
        .ac-form-title em { font-style:italic; font-weight:400; color:#B08D57; }
        .ac-form-sub {
          font-size:12px; font-weight:300; color:#8A7E72;
          letter-spacing:0.03em; margin-bottom:30px; line-height:1.65;
        }

        /* Fields */
        .ac-field { margin-bottom:16px; }
        .ac-label {
          font-size:9px; font-weight:600; letter-spacing:0.22em;
          text-transform:uppercase; color:#5C5247; display:block; margin-bottom:6px;
        }
        .ac-input-wrap { position:relative; }
        .ac-input-icon {
          position:absolute; left:12px; top:50%; transform:translateY(-50%);
          color:#B08D57; pointer-events:none;
        }
        .ac-input {
          width:100%; background:#fff; border:1px solid rgba(176,141,87,0.25);
          padding:12px 14px; font-family:'Jost',sans-serif;
          font-size:13px; font-weight:400; color:#1C1A18;
          outline:none; transition:border-color 0.2s,box-shadow 0.2s;
          border-radius:1px; box-sizing:border-box;
        }

        .ac-order-row:hover {
        background: rgba(176,141,87,0.03);
        border-radius: 2px;
      }
        .ac-input:focus { border-color:#B08D57; box-shadow:0 0 0 3px rgba(176,141,87,0.1); }
        .ac-input:disabled { opacity:0.6; cursor:not-allowed; }
        .ac-eye {
          position:absolute; right:12px; top:50%; transform:translateY(-50%);
          background:none; border:none; outline:none; box-shadow:none;
          cursor:pointer; color:#8A7E72;
          transition:color 0.2s; padding:0;
        }
        .ac-eye:hover { color:#B08D57; }
        .ac-forgot {
          display:block; text-align:right; margin-top:4px;
          font-size:10px; font-weight:500; letter-spacing:0.1em;
          color:#B08D57; text-decoration:none; transition:opacity 0.2s;
        }
        .ac-forgot:hover { opacity:0.7; }
        .ac-error {
          font-size:11px; color:#C0392B; letter-spacing:0.05em;
          margin-bottom:14px; background:rgba(192,57,43,0.06);
          padding:9px 12px; border-left:2px solid #C0392B;
        }
        .ac-success {
          font-size:11px; color:#2E7D32; letter-spacing:0.05em;
          margin-bottom:14px; background:rgba(46,125,50,0.06);
          padding:9px 12px; border-left:2px solid #2E7D32;
        }
        .ac-submit {
          width:100%; background:#1C1A18; color:#FAF8F4; border:none;
          font-family:'Jost',sans-serif; font-size:10px; font-weight:600;
          letter-spacing:0.22em; text-transform:uppercase; padding:15px;
          cursor:pointer; margin-top:8px;
          position:relative; overflow:hidden; transition:color 0.28s;
          display:flex; align-items:center; justify-content:center; gap:8px;
        }
        .ac-submit::after {
          content:''; position:absolute; inset:0; background:#B08D57;
          transform:translateX(-101%);
          transition:transform 0.28s cubic-bezier(0.4,0,0.2,1); z-index:0;
        }
        .ac-submit:hover:not(:disabled)::after { transform:translateX(0); }
        .ac-submit:disabled { opacity:0.7; cursor:not-allowed; }
        .ac-submit span { position:relative; z-index:1; }
        .ac-submit svg { position:relative; z-index:1; animation:spin 0.8s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }
        .ac-divider { display:flex; align-items:center; gap:12px; margin:20px 0; }
        .ac-divider::before,.ac-divider::after { content:''; flex:1; height:1px; background:rgba(176,141,87,0.2); }
        .ac-divider span { font-size:9px; font-weight:500; letter-spacing:0.2em; text-transform:uppercase; color:#AEA39A; }
        .ac-toggle { text-align:center; font-size:12px; font-weight:300; color:#8A7E72; letter-spacing:0.03em; }
        .ac-toggle button {
          background:none; border:none; cursor:pointer; font-family:'Jost',sans-serif;
          font-size:12px; font-weight:600; color:#B08D57; letter-spacing:0.06em;
          text-decoration:underline; text-underline-offset:3px; transition:opacity 0.2s;
        }
        .ac-toggle button:hover { opacity:0.7; }

        /* PROFILE */
        .ac-profile-wrap {
          position:relative; z-index:1; max-width:1100px; margin:0 auto;
          padding:40px clamp(20px,5vw,56px) 80px;
          display:grid; grid-template-columns:260px 1fr; gap:28px; align-items:start;
        }
        .ac-sidebar {
          background:#fff; border:1px solid rgba(176,141,87,0.12);
          border-radius:2px; overflow:hidden; position:sticky; top: 20px; 
        }
        .ac-avatar-block {
          background:linear-gradient(135deg,#1C1A18 0%,#2E2B26 100%);
          padding:28px 20px; display:flex; flex-direction:column;
          align-items:center; gap:12px; text-align:center; position:relative;
        }
        .ac-avatar-block::before {
          content:''; position:absolute; top:0; left:20%; right:20%; height:2px;
          background:linear-gradient(to right,transparent,#B08D57,transparent);
        }
        .ac-avatar-circle {
          width:72px; height:72px; border-radius:50%;
          background:linear-gradient(135deg,#B08D57,#8A6D3E);
          display:flex; align-items:center; justify-content:center;
          font-family:'Playfair Display',serif; font-size:26px; font-weight:700;
          color:#FAF8F4; border:2px solid rgba(176,141,87,0.4); flex-shrink:0;
        }
        .ac-avatar-name { font-family:'Playfair Display',serif; font-size:16px; font-weight:600; color:#FAF8F4; letter-spacing:-0.01em; }
        .ac-avatar-email { font-size:10px; font-weight:300; color:rgba(250,248,244,0.5); letter-spacing:0.05em; }
        .ac-avatar-edit {
          background:rgba(176,141,87,0.15); border:1px solid rgba(176,141,87,0.3);
          color:#B08D57; font-family:'Jost',sans-serif; font-size:8px; font-weight:600;
          letter-spacing:0.18em; text-transform:uppercase; padding:5px 14px;
          cursor:pointer; transition:background 0.2s;
          display:flex; align-items:center; gap:5px; border-radius:1px;
        }
        .ac-avatar-edit:hover { background:rgba(176,141,87,0.28); }
        .ac-nav-tabs { padding:8px 0; }
        .ac-nav-tab {
          width:100%; display:flex; align-items:center; justify-content:space-between;
          padding:13px 20px; background:none; border:none;
          font-family:'Jost',sans-serif; font-size:11px; font-weight:500;
          letter-spacing:0.1em; text-transform:uppercase; color:#5C5247;
          cursor:pointer; transition:background 0.2s,color 0.2s;
          border-left:2px solid transparent;
        }
        .ac-nav-tab:hover { background:#FDF9F4; color:#B08D57; }
        .ac-nav-tab--active { background:#FDF9F4; color:#B08D57; border-left-color:#B08D57; }
        .ac-nav-tab-left { display:flex; align-items:center; gap:10px; }
        .ac-logout-btn {
          width:100%; display:flex; align-items:center; gap:10px;
          padding:14px 20px; background:none; border:none;
          border-top:1px solid rgba(176,141,87,0.1);
          font-family:'Jost',sans-serif; font-size:11px; font-weight:500;
          letter-spacing:0.1em; text-transform:uppercase;
          color:#C0392B; cursor:pointer; transition:background 0.2s;
        }
        .ac-logout-btn:hover { background:rgba(192,57,43,0.05); }

        /* Panel */
        .ac-content { display:flex; flex-direction:column; gap:16px; }
        .ac-panel { background:#fff; border:1px solid rgba(176,141,87,0.12); border-radius:2px; overflow:hidden; }
        .ac-panel-header {
          padding:18px 22px; border-bottom:1px solid rgba(176,141,87,0.1);
          display:flex; align-items:center; justify-content:space-between;
        }
        .ac-panel-title { font-family:'Playfair Display',serif; font-size:17px; font-weight:600; color:#1C1A18; letter-spacing:-0.01em; }
        .ac-panel-title em { font-style:italic; font-weight:400; color:#B08D57; }
        .ac-panel-body { padding:20px 22px; }

        /* Spinner */
        .ac-spinner-wrap { display:flex; justify-content:center; align-items:center; padding:40px; }
        .ac-spinner { color:#B08D57; animation:spin 0.9s linear infinite; }

        /* Empty state */
        .ac-empty { display:flex; flex-direction:column; align-items:center; gap:10px; padding:48px 20px; text-align:center; }
        .ac-empty-icon { width:56px; height:56px; border-radius:50%; background:rgba(176,141,87,0.08); display:flex; align-items:center; justify-content:center; color:#B08D57; }
        .ac-empty-title { font-family:'Playfair Display',serif; font-size:16px; font-weight:600; color:#1C1A18; }
        .ac-empty-sub { font-size:12px; font-weight:300; color:#8A7E72; }

        /* Orders */
        .ac-order-row {
          display:flex; align-items:center; justify-content:space-between;
          padding:14px 0; border-bottom:1px solid rgba(176,141,87,0.08); flex-wrap:wrap; gap:10px;
        }
        .ac-order-row:last-child { border-bottom:none; }
        .ac-order-id { font-family:'Playfair Display',serif; font-size:15px; font-weight:600; color:#1C1A18; }
        .ac-order-meta { font-size:11px; color:#8A7E72; letter-spacing:0.04em; margin-top:2px; }
        .ac-order-total { font-family:'Playfair Display',serif; font-size:15px; font-weight:700; color:#B08D57; }
        .ac-order-status { font-size:9px; font-weight:700; letter-spacing:0.16em; text-transform:uppercase; padding:4px 10px; border-radius:100px; }

        /* Wishlist */
        .ac-wish-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
        .ac-wish-card { border:1px solid rgba(176,141,87,0.12); border-radius:2px; overflow:hidden; transition:box-shadow 0.25s; position:relative; }
        .ac-wish-card:hover { box-shadow:0 6px 24px rgba(28,26,24,0.08); }
        .ac-wish-img { aspect-ratio:1/1; overflow:hidden; background:#F0EAE0; }
        .ac-wish-img img { width:100%; height:100%; object-fit:cover; display:block; transition:transform 0.5s; }
        .ac-wish-card:hover .ac-wish-img img { transform:scale(1.05); }
        .ac-wish-info { padding:10px 12px; }
        .ac-wish-name { font-size:12px; font-weight:500; color:#1C1A18; margin-bottom:4px; }
        .ac-wish-price { font-family:'Playfair Display',serif; font-size:13px; font-weight:700; color:#B08D57; }
        .ac-wish-remove {
          position:absolute; top:8px; right:8px;
          background:rgba(250,248,244,0.92); border:none; border-radius:50%;
          width:26px; height:26px; display:flex; align-items:center; justify-content:center;
          cursor:pointer; font-size:12px; color:#C0392B; transition:background 0.2s; opacity:0;
        }
        .ac-wish-card:hover .ac-wish-remove { opacity:1; }
        .ac-wish-remove:hover { background:#fff; }

        /* Addresses */
        .ac-address-card {
          border:1px solid rgba(176,141,87,0.2); padding:16px 18px;
          border-radius:1px; margin-bottom:12px;
          display:flex; justify-content:space-between; align-items:flex-start; gap:12px;
        }
        .ac-address-label { font-size:8px; font-weight:700; letter-spacing:0.2em; text-transform:uppercase; color:#B08D57; margin-bottom:6px; }
        .ac-address-text { font-size:13px; font-weight:300; color:#5C5247; line-height:1.7; }
        .ac-address-default { font-size:8px; font-weight:600; letter-spacing:0.14em; text-transform:uppercase; color:#2E7D32; margin-top:6px; }
        .ac-address-del {
          background:none; border:none; color:#C0392B; cursor:pointer;
          font-size:11px; font-weight:500; letter-spacing:0.1em;
          text-transform:uppercase; transition:opacity 0.2s; flex-shrink:0; padding:0;
        }
        .ac-address-del:hover { opacity:0.7; }
        .ac-add-address {
          width:100%; border:1px dashed rgba(176,141,87,0.35); background:none; padding:14px;
          cursor:pointer; font-family:'Jost',sans-serif; font-size:10px; font-weight:600;
          letter-spacing:0.18em; text-transform:uppercase; color:#B08D57; transition:background 0.2s;
        }
        .ac-add-address:hover { background:rgba(176,141,87,0.05); }

        /* Security */
        .ac-security-row {
          display:flex; align-items:center; justify-content:space-between;
          padding:14px 0; border-bottom:1px solid rgba(176,141,87,0.08); flex-wrap:wrap; gap:10px;
        }
        .ac-security-row:last-child { border-bottom:none; }
        .ac-security-label { font-size:12px; font-weight:500; color:#1C1A18; }
        .ac-security-sub { font-size:11px; font-weight:300; color:#8A7E72; margin-top:2px; }
        .ac-security-btn {
          background:none; border:1px solid rgba(176,141,87,0.3); color:#B08D57;
          font-family:'Jost',sans-serif; font-size:9px; font-weight:600;
          letter-spacing:0.16em; text-transform:uppercase; padding:7px 14px;
          cursor:pointer; transition:background 0.2s;
        }
        .ac-security-btn:hover { background:rgba(176,141,87,0.08); }

        /* Modal */
       .ac-modal-backdrop {
        position:fixed; inset:0; background:rgba(12,9,5,0.55);
        z-index:1000; display:flex; align-items:flex-start; justify-content:center;
        padding:170px 20px 40px; overflow-y:auto;
      }
        .ac-modal {
          background:#FAF8F4; border:1px solid rgba(176,141,87,0.2);
          border-radius:2px; width:100%; max-width:440px;
          padding:28px 28px 24px; margin:auto;
        }
        .ac-modal-title { font-family:'Playfair Display',serif; font-size:20px; font-weight:600; color:#1C1A18; margin-bottom:20px; }
        .ac-modal-title em { font-style:italic; font-weight:400; color:#B08D57; }
        .ac-modal-footer { display:flex; gap:10px; margin-top:20px; justify-content:flex-end; }
        .ac-modal-cancel {
          background:none; border:1px solid rgba(176,141,87,0.25); color:#8A7E72;
          font-family:'Jost',sans-serif; font-size:10px; font-weight:600;
          letter-spacing:0.16em; text-transform:uppercase; padding:10px 18px;
          cursor:pointer; transition:border-color 0.2s;
        }
        .ac-modal-cancel:hover { border-color:#B08D57; color:#B08D57; }
        .ac-modal-save {
          background:#1C1A18; color:#FAF8F4; border:none; font-family:'Jost',sans-serif;
          font-size:10px; font-weight:600; letter-spacing:0.16em; text-transform:uppercase;
          padding:10px 20px; cursor:pointer; display:flex; align-items:center; gap:6px;
          position:relative; overflow:hidden; transition:color 0.28s;
        }
        .ac-modal-save::after {
          content:''; position:absolute; inset:0; background:#B08D57;
          transform:translateX(-101%); transition:transform 0.28s cubic-bezier(0.4,0,0.2,1); z-index:0;
        }
        .ac-modal-save:hover:not(:disabled)::after { transform:translateX(0); }
        .ac-modal-save:disabled { opacity:0.7; cursor:not-allowed; }
        .ac-modal-save span, .ac-modal-save svg { position:relative; z-index:1; }
        .ac-modal-save svg { animation:spin 0.8s linear infinite; }
        .ac-addr-grid { display:grid; grid-template-columns:1fr 1fr; gap:0 12px; }
        .ac-checkbox-row { display:flex; align-items:center; gap:8px; margin-bottom:16px; cursor:pointer; }
        .ac-checkbox-row input { accent-color:#B08D57; width:14px; height:14px; cursor:pointer; }
        .ac-checkbox-row span { font-size:11px; font-weight:500; color:#5C5247; letter-spacing:0.08em; }

        /* Responsive */
        @media (max-width:860px) {
          .ac-auth-wrap { grid-template-columns:1fr; }
          .ac-auth-left { display:none; }
          .ac-profile-wrap { grid-template-columns:1fr; }
          .ac-sidebar { position:static; }
          .ac-nav-tabs { display:flex; flex-wrap:wrap; }
          .ac-nav-tab { flex:1; min-width:120px; border-left:none; border-bottom:2px solid transparent; }
          .ac-nav-tab--active { border-bottom-color:#B08D57; border-left:none; }
          .ac-wish-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:480px) {
          .ac-page { padding-top:136px; }
          .ac-wish-grid { grid-template-columns:1fr 1fr; }
          .ac-addr-grid { grid-template-columns:1fr; }
        }
      `}</style>

      <div className="ac-page">

        {/* ══ AUTH ══ */}
        <AnimatePresence mode="wait">
          {(mode === 'login' || mode === 'register') && (
            <motion.div key="auth" className="ac-auth-wrap"
              initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              transition={{ duration:0.45 }}
            >
              {/* Left image */}
              <div className="ac-auth-left">
                <img src="https://images.pexels.com/photos/3266700/pexels-photo-3266700.jpeg?auto=compress&cs=tinysrgb&w=900" alt="Aamira jewellery" />
                <div className="ac-auth-left-overlay" />
                <div className="ac-auth-left-content" ref={heroRef}>
                  <motion.p className="ac-auth-left-eyebrow"
                    initial={{ opacity:0, x:-16 }} animate={heroInView ? { opacity:1, x:0 } : {}}
                    transition={{ duration:0.6 }}
                  >Aamira Jewellery</motion.p>
                  <motion.h2 className="ac-auth-left-title"
                    initial={{ opacity:0, y:20 }} animate={heroInView ? { opacity:1, y:0 } : {}}
                    transition={{ duration:0.7, delay:0.1 }}
                  >Where<em>Elegance Begins</em></motion.h2>
                  <motion.p className="ac-auth-left-sub"
                    initial={{ opacity:0 }} animate={heroInView ? { opacity:1 } : {}}
                    transition={{ duration:0.6, delay:0.25 }}
                  >Sign in to access your orders, wishlist, and exclusive member offers.</motion.p>
                </div>
              </div>

              {/* Right form */}
              <div className="ac-auth-right">
                <AnimatePresence mode="wait">

  {/* ── OTP Screen ── */}
  {otpMode && (
    <motion.div key="otp" className="ac-form-box"
      initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
      exit={{ opacity:0, y:-16 }} transition={{ duration:0.4 }}
    >
      <p className="ac-form-eyebrow">Almost There</p>
      <h1 className="ac-form-title">Verify <em>Email</em></h1>
      <p className="ac-form-sub">
        We sent a 6-digit code to <strong>{otpEmail}</strong>. 
        Check your inbox and enter it below.
      </p>

      {error   && <div className="ac-error">{error}</div>}
      {success && <div className="ac-success">{success}</div>}

      <form onSubmit={handleVerifyOTP}>
        <div className="ac-field">
          <label className="ac-label">Verification Code</label>
          <div className="ac-input-wrap">
            <input
              className="ac-input"
              type="text"
              inputMode="numeric"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              disabled={loading}
              style={{ 
                textAlign: 'center', fontSize: 26, 
                letterSpacing: '0.5em', paddingLeft: 14,
              }}
            />
          </div>
        </div>
        <button className="ac-submit" type="submit" disabled={loading || otp.length !== 6}>
          {loading && <Loader2 size={14} />}
          <span>{loading ? 'Verifying…' : 'Verify & Create Account'}</span>
        </button>
      </form>

      <div className="ac-divider"><span>or</span></div>
      <p className="ac-toggle">
        Wrong email?{' '}
        <button onClick={() => { 
          setOtpMode(false); setMode('register'); 
          setError(''); setSuccess(''); setOtp(''); 
        }}>
          Go Back
        </button>
      </p>
    </motion.div>
  )}

  {/* ── Login / Register ── */}
                {!otpMode && (
                  <motion.div key={mode} className="ac-form-box"
                    initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
                    exit={{ opacity:0, y:-16 }} transition={{ duration:0.4 }}
                  >
                    <p className="ac-form-eyebrow">
                      {mode === 'login' ? 'Welcome Back' : 'Join Aamira'}
                    </p>
                    <h1 className="ac-form-title">
                      {mode === 'login' ? <>Sign <em>In</em></> : <>Create <em>Account</em></>}
                    </h1>
                    <p className="ac-form-sub">
                      {mode === 'login' 
                        ? 'Enter your details to access your account.' 
                        : 'Join us for exclusive offers, early access & more.'}
                    </p>

                    {error   && <div className="ac-error">{error}</div>}
                    {success && <div className="ac-success">{success}</div>}

                    <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
                      {mode === 'register' && (
                        <Input 
                          label="Full Name" 
                          value={name} 
                          onChange={e => setName(e.target.value)} 
                          icon={User} 
                          disabled={loading} 
                        />
                      )}
                      <Input 
                        label="Email Address" 
                        type="email" 
                        value={email} 
                        onChange={e => setEmail(e.target.value)} 
                        icon={Mail} 
                        disabled={loading} 
                      />
                      <Input
                        label={mode === 'register' 
                          ? 'Password (min 8 characters)' 
                          : 'Password'}
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        icon={Shield}
                        disabled={loading}
                      extra={mode === 'login' && (
                      <button
                        type="button"
                        className="ac-forgot"
                        onClick={() => {
                          setForgotMode(true);
                          setForgotStep('email');
                          setForgotEmail(email);
                          setForgotError(''); setForgotSuccess('');
                        }}
                      >
                        Forgot password?
                      </button>
                    )}
                      />
                      {/* Password strength indicator — register only */}
                      {mode === 'register' && password.length > 0 && (
                        <div style={{ marginTop: -10, marginBottom: 14 }}>
                          <div style={{ 
                            height: 3, background: '#f0ebe3', 
                            borderRadius: 2, overflow: 'hidden', marginBottom: 4 
                          }}>
                            <div style={{
                              height: '100%', borderRadius: 2,
                              width: password.length >= 8 
                                ? password.length >= 12 ? '100%' : '66%' 
                                : '33%',
                              background: password.length >= 8 
                                ? password.length >= 12 ? '#2E7D32' : '#B08D57' 
                                : '#C0392B',
                              transition: 'all 0.3s',
                            }} />
                          </div>
                          <p style={{ 
                            fontSize: 10, color: password.length >= 8 
                              ? password.length >= 12 ? '#2E7D32' : '#B08D57' 
                              : '#C0392B',
                            letterSpacing: '0.08em',
                          }}>
                            {password.length >= 12 
                              ? '✓ Strong password' 
                              : password.length >= 8 
                                ? '~ Good password' 
                                : `✗ Too short (${password.length}/8)`}
                          </p>
                        </div>
                      )}
                      <button className="ac-submit" type="submit" disabled={loading}>
                        {loading && <Loader2 size={14} />}
                        <span>
                          {loading 
                            ? 'Please wait…' 
                            : mode === 'login' ? 'Sign In' : 'Send OTP'}
                        </span>
                      </button>
                    </form>

                    <div className="ac-divider"><span>or</span></div>
                    <p className="ac-toggle">
                      {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                      <button onClick={() => { 
                        setMode(mode === 'login' ? 'register' : 'login'); 
                        setError(''); setSuccess(''); 
                      }}>
                        {mode === 'login' ? 'Register' : 'Sign In'}
                      </button>
                    </p>
                  </motion.div>
                )}

              </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* ══ PROFILE ══ */}
          {mode === 'profile' && user && (
            <motion.div key="profile"
              initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
              exit={{ opacity:0 }} transition={{ duration:0.45 }}
            >
              <div className="ac-profile-wrap">

                {/* Sidebar */}
                <div className="ac-sidebar">
                  <div className="ac-avatar-block">
                    <div className="ac-avatar-circle">
                      {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="ac-avatar-name">{user.name}</p>
                      <p className="ac-avatar-email">{user.email}</p>
                    </div>
                    <button className="ac-avatar-edit" onClick={openEdit}>
                      <Edit2 size={10} /> Edit Profile
                    </button>
                  </div>

                  <div className="ac-nav-tabs">
                    {PROFILE_TABS.map(t => (
                      <button key={t.id}
                        className={`ac-nav-tab ${tab === t.id ? 'ac-nav-tab--active' : ''}`}
                        onClick={() => setTab(t.id)}
                      >
                        <span className="ac-nav-tab-left"><t.icon size={14} /> {t.label}</span>
                        <ChevronRight size={12} style={{ opacity:0.4 }} />
                      </button>
                    ))}
                  </div>

                  <button className="ac-logout-btn" onClick={handleLogout}>
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>

                {/* Content */}
                <div className="ac-content">
                  <AnimatePresence mode="wait">

                    {/* ── ORDERS ── */}
                    {tab === 'orders' && (
                      <motion.div key="orders" className="ac-panel"
                        initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                        exit={{ opacity:0 }} transition={{ duration:0.35 }}
                      >
                        <div className="ac-panel-header">
                          <h2 className="ac-panel-title">My <em>Orders</em></h2>
                        </div>
                        <div className="ac-panel-body">
                          {tabLoading ? <Spinner /> : tabError ? (
                            <div className="ac-error">{tabError}</div>
                          ) : orders.length === 0 ? (
                            <EmptyState icon={ShoppingBag} title="No orders yet" sub="Your order history will appear here." />
                          ) : orders.map(o => {
  const sc = statusColor(o.status);
  const date = o.created_at
    ? new Date(o.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })
    : '';
  const total = o.total != null ? formatPrice(o.total) : '—';
  const items = Array.isArray(o.items) ? o.items : [];

return (
  <Link
    key={o.id}
    to={`/orders/${o.id}`}
    style={{ textDecoration:'none', color:'inherit', display:'block' }}
  >
    <div className="ac-order-row" style={{ flexDirection:'column', alignItems:'stretch', cursor:'pointer' }}>

      {/* Order header — no changes */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }}>
        <div>
          <p className="ac-order-id">
          {items.length > 0
            ? items.map(i => i.name).join(', ').slice(0, 60) + (items.map(i => i.name).join(', ').length > 60 ? '…' : '')
            : `#${o.order_number}`}
        </p>
        <p className="ac-order-meta">{date} · {items.length} item(s) · #{o.order_number}</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
          <span className="ac-order-total">{total}</span>
          <span className="ac-order-status" style={{ background:sc.bg, color:sc.color }}>
            {o.status || 'Pending'}
          </span>
        </div>
      </div>

      {/* Product rows — no changes */}
      {items.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginTop:'12px', paddingTop:'12px', borderTop:'1px solid rgba(176,141,87,0.08)' }}>
          {items.map((item, idx) => (
            <div key={idx} style={{ display:'flex', alignItems:'center', gap:'12px' }}>
              <div style={{ width:'56px', height:'56px', borderRadius:'2px', overflow:'hidden', background:'#F0EAE0', flexShrink:0 }}>
                {item.image ? (
                  <img src={item.image} alt={item.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                ) : (
                  <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <ShoppingBag size={18} style={{ color:'#B08D57', opacity:0.4 }} />
                  </div>
                )}
              </div>
              <div style={{ flex:1 }}>
                <p style={{ fontSize:'13px', fontWeight:'500', color:'#1C1A18', marginBottom:'3px' }}>
                  {item.name || 'Product'}
                </p>
                <p style={{ fontSize:'11px', fontWeight:'300', color:'#8A7E72' }}>
                  Qty: {item.qty || 1}
                  {item.size  ? ` · Size: ${item.size}`  : ''}
                  {item.color ? ` · ${item.color}`        : ''}
                </p>
              </div>
              {item.price && (
                <p style={{ fontFamily:'Playfair Display,serif', fontSize:'13px', fontWeight:'700', color:'#B08D57', flexShrink:0 }}>
                  {formatPrice(item.price)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  </Link>
);
})}
                        </div>
                      </motion.div>
                    )}

                    {/* ── WISHLIST ── */}
                    {tab === 'wishlist' && (
                      <motion.div key="wishlist" className="ac-panel"
                        initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                        exit={{ opacity:0 }} transition={{ duration:0.35 }}
                      >
                        <div className="ac-panel-header">
                          <h2 className="ac-panel-title">My <em>Wishlist</em></h2>
                        </div>
                        <div className="ac-panel-body">
                          {tabLoading ? <Spinner /> : tabError ? (
                            <div className="ac-error">{tabError}</div>
) : wishlist.length === 0 ? (
  <EmptyState icon={Heart} title="Wishlist is empty" sub="Save items you love and find them here." />
) : (
  <div className="ac-wish-grid">
    {wishlist.map(w => (
      <div key={w.id} className="ac-wish-card">
        <Link to={`/products/${w.id}`} style={{ textDecoration:'none' }}>
          <div className="ac-wish-img">
            {w.image
              ? <img src={w.image} alt={w.name} />
              : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Heart size={24} style={{ color:'#B08D57', opacity:0.3 }} />
                </div>
            }
          </div>
          <div className="ac-wish-info">
            <p className="ac-wish-name">{w.name || 'Product'}</p>
            <p className="ac-wish-price">
              {w.price ? formatPrice(w.price) : '—'}
            </p>
          </div>
        </Link>
        {/* Remove button */}
        <button
          className="ac-wish-remove"
          title="Remove from wishlist"
          onClick={() => handleRemoveWish(w.id)}
        >✕</button>
        {/* Add to cart button */}
        <div style={{
          padding:'0 10px 10px',
          borderTop:'1px solid rgba(176,141,87,0.08)',
          marginTop:'4px',
          paddingTop:'8px',
        }}>
          <Link
            to={`/products/${w.id}`}
            style={{
              display:'block', textAlign:'center',
              background:'#1C1A18', color:'#FAF8F4',
              fontSize:'8px', fontWeight:'600',
              letterSpacing:'0.18em', textTransform:'uppercase',
              padding:'8px', textDecoration:'none',
              transition:'background 0.2s',
            }}
            onMouseEnter={e => e.target.style.background='#B08D57'}
            onMouseLeave={e => e.target.style.background='#1C1A18'}
          >View Product</Link>
        </div>
      </div>
    ))}
  </div>
)}
                        </div>
                      </motion.div>
                    )}

                    {/* ── ADDRESSES ── */}
                    {tab === 'address' && (
                      <motion.div key="address" className="ac-panel"
                        initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                        exit={{ opacity:0 }} transition={{ duration:0.35 }}
                      >
                        <div className="ac-panel-header">
                          <h2 className="ac-panel-title">Saved <em>Addresses</em></h2>
                        </div>
                        <div className="ac-panel-body">
                          {tabLoading ? <Spinner /> : tabError ? (
                            <div className="ac-error">{tabError}</div>
                          ) : (
                            <>
                              {addresses.length === 0 && (
                                <EmptyState icon={MapPin} title="No addresses saved" sub="Add a delivery address to checkout faster." />
                              )}
                              {addresses.map(a => (
                                <div key={a.id} className="ac-address-card">
                                  <div>
                                    {a.label && <p className="ac-address-label">{a.label}</p>}
                                    <p className="ac-address-text">
                                      {a.full_name && <>{a.full_name}<br /></>}
                                      {a.line_1}{a.line_2 ? `, ${a.line_2}` : ''}<br />
                                      {a.city}{a.state ? `, ${a.state}` : ''}{a.pincode ? ` — ${a.pincode}` : ''}<br />
                                      {a.country || 'India'}
                                    </p>
                                    {a.is_default && <p className="ac-address-default">✓ Default</p>}
                                  </div>
                                  <button className="ac-address-del" onClick={() => handleDeleteAddress(a.id)}>Remove</button>
                                </div>
                              ))}
                              <button className="ac-add-address" onClick={() => setAddrOpen(true)}>
                                + Add New Address
                              </button>
                            </>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* ── SECURITY ── */}
                    {tab === 'security' && (
                      <motion.div key="security" className="ac-panel"
                        initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                        exit={{ opacity:0 }} transition={{ duration:0.35 }}
                      >
                        <div className="ac-panel-header">
                          <h2 className="ac-panel-title">Account <em>Security</em></h2>
                        </div>
                        <div className="ac-panel-body">
                          <div className="ac-security-row">
                            <div>
                              <p className="ac-security-label">Email Address</p>
                              <p className="ac-security-sub">{user.email}</p>
                            </div>
                          </div>
                          <div className="ac-security-row">
                            <div>
                              <p className="ac-security-label">Password</p>
                              <p className="ac-security-sub">Update your account password</p>
                            </div>
                            <button className="ac-security-btn" onClick={() => setPwOpen(true)}>Update</button>
                          </div>
                          <div className="ac-security-row">
                            <div>
                              <p className="ac-security-label">Phone Number</p>
                              <p className="ac-security-sub">{user.phone || 'Not added yet'}</p>
                            </div>
                            <button className="ac-security-btn" onClick={openEdit}>
                              {user.phone ? 'Change' : 'Add'}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}

                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ══ EDIT PROFILE MODAL ══ */}
        <AnimatePresence>
          {editOpen && (
            <motion.div className="ac-modal-backdrop"
              initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              onClick={() => setEditOpen(false)}
            >
              <motion.div className="ac-modal"
                initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:12 }}
                onClick={e => e.stopPropagation()}
              >
                <h3 className="ac-modal-title">Edit <em>Profile</em></h3>
                <Input label="Full Name" value={editName} onChange={e => setEditName(e.target.value)} icon={User} />
                <Input label="Phone Number" type="tel" value={editPhone} onChange={e => setEditPhone(e.target.value)} icon={Mail} />
                {editError && <div className="ac-error">{editError}</div>}
                <div className="ac-modal-footer">
                  <button className="ac-modal-cancel" onClick={() => setEditOpen(false)}>Cancel</button>
                  <button className="ac-modal-save" onClick={handleSaveProfile} disabled={editSaving}>
                    {editSaving && <Loader2 size={13} />}<span>Save</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ══ CHANGE PASSWORD MODAL ══ */}
        <AnimatePresence>
          {pwOpen && (
            <motion.div className="ac-modal-backdrop"
              initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              onClick={() => setPwOpen(false)}
            >
              <motion.div className="ac-modal"
                initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:12 }}
                onClick={e => e.stopPropagation()}
              >
                <h3 className="ac-modal-title">Change <em>Password</em></h3>
                <Input label="New Password (min 6 characters)" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} icon={Shield} />
                {pwMsg && <div className={pwMsg.includes('success') ? 'ac-success' : 'ac-error'}>{pwMsg}</div>}
                <div className="ac-modal-footer">
                  <button className="ac-modal-cancel" onClick={() => { setPwOpen(false); setPwMsg(''); setNewPw(''); }}>Cancel</button>
                  <button className="ac-modal-save" onClick={handleChangePassword} disabled={pwSaving}>
                    {pwSaving && <Loader2 size={13} />}<span>Update</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ══ ADD ADDRESS MODAL ══ */}
        {/* Fields match backend exactly: label, full_name, phone, line_1, line_2, city, state, pincode, is_default */}
        <AnimatePresence>
          {addrOpen && (
            <motion.div className="ac-modal-backdrop"
              initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              onClick={() => setAddrOpen(false)}
            >
              <motion.div className="ac-modal"
                initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:12 }}
                onClick={e => e.stopPropagation()}
              >
                <h3 className="ac-modal-title">Add <em>Address</em></h3>
                <Input label="Label (e.g. Home, Office)" value={addrForm.label}
                  onChange={e => setAddrForm(p => ({...p, label: e.target.value}))} icon={MapPin} />
                <Input label="Full Name" value={addrForm.full_name}
                  onChange={e => setAddrForm(p => ({...p, full_name: e.target.value}))} icon={User} />
                <Input label="Phone" type="tel" value={addrForm.phone}
                  onChange={e => setAddrForm(p => ({...p, phone: e.target.value}))} icon={Mail} />
                <Input label="Address Line 1 *" value={addrForm.line_1}
                  onChange={e => setAddrForm(p => ({...p, line_1: e.target.value}))} />
                <Input label="Address Line 2 (optional)" value={addrForm.line_2}
                  onChange={e => setAddrForm(p => ({...p, line_2: e.target.value}))} />
                <div className="ac-addr-grid">
                  <Input label="City *" value={addrForm.city}
                    onChange={e => setAddrForm(p => ({...p, city: e.target.value}))} />
                  <Input label="State" value={addrForm.state}
                    onChange={e => setAddrForm(p => ({...p, state: e.target.value}))} />
                </div>
                <Input label="Pincode *" value={addrForm.pincode}
                  onChange={e => setAddrForm(p => ({...p, pincode: e.target.value}))} />
                <Input label="Country" value={addrForm.country}
                  onChange={e => setAddrForm(p => ({...p, country: e.target.value}))} />

                <label className="ac-checkbox-row"></label>
                <label className="ac-checkbox-row">
                  <input type="checkbox" checked={addrForm.is_default}
                    onChange={e => setAddrForm(p => ({...p, is_default: e.target.checked}))} />
                  <span>Set as default address</span>
                </label>
                {addrError && <div className="ac-error">{addrError}</div>}
                <div className="ac-modal-footer">
                  <button className="ac-modal-cancel" onClick={() => { setAddrOpen(false); setAddrError(''); }}>Cancel</button>
                  <button className="ac-modal-save" onClick={handleAddAddress} disabled={addrSaving}>
                    {addrSaving && <Loader2 size={13} />}<span>Save Address</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      {/* ══ FORGOT PASSWORD MODAL ══ */}
        <AnimatePresence>
          {forgotMode && (
            <motion.div className="ac-modal-backdrop"
              initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              onClick={() => { setForgotMode(false); setForgotStep('email'); setForgotError(''); setForgotSuccess(''); }}
            >
              <motion.div className="ac-modal"
                initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:12 }}
                onClick={e => e.stopPropagation()}
              >
                {forgotStep === 'email' && (
                  <>
                    <h3 className="ac-modal-title">Forgot <em>Password</em></h3>
                    <p style={{ fontSize:12, color:'#8A7E72', marginBottom:20, lineHeight:1.6 }}>
                      Enter your registered email and we'll send you a verification code.
                    </p>
                    {forgotError   && <div className="ac-error">{forgotError}</div>}
                    {forgotSuccess && <div className="ac-success">{forgotSuccess}</div>}
                    <form onSubmit={handleForgotSendOtp}>
                      <Input label="Email Address" type="email" value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)} icon={Mail} disabled={forgotLoading} />
                      <div className="ac-modal-footer">
                        <button type="button" className="ac-modal-cancel"
                          onClick={() => { setForgotMode(false); setForgotError(''); }}>Cancel</button>
                        <button type="submit" className="ac-modal-save" disabled={forgotLoading}>
                          {forgotLoading && <Loader2 size={13} />}<span>Send OTP</span>
                        </button>
                      </div>
                    </form>
                  </>
                )}

                {forgotStep === 'otp' && (
                  <>
                    <h3 className="ac-modal-title">Verify <em>Code</em></h3>
                    <p style={{ fontSize:12, color:'#8A7E72', marginBottom:20, lineHeight:1.6 }}>
                      We sent a 6-digit code to <strong>{forgotEmail}</strong>. Enter it below.
                    </p>
                    {forgotError   && <div className="ac-error">{forgotError}</div>}
                    {forgotSuccess && <div className="ac-success">{forgotSuccess}</div>}
                    <form onSubmit={handleForgotVerifyOtp}>
                      <div className="ac-field">
                        <label className="ac-label">Verification Code</label>
                        <input
                          className="ac-input"
                          type="text"
                          inputMode="numeric"
                          value={forgotOtp}
                          onChange={e => setForgotOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                          placeholder="000000"
                          maxLength={6}
                          disabled={forgotLoading}
                          style={{ textAlign:'center', fontSize:26, letterSpacing:'0.5em' }}
                        />
                      </div>
                      <div className="ac-modal-footer">
                        <button type="button" className="ac-modal-cancel"
                          onClick={() => setForgotStep('email')}>Back</button>
                        <button type="submit" className="ac-modal-save"
                          disabled={forgotLoading || forgotOtp.length !== 6}>
                          {forgotLoading && <Loader2 size={13} />}<span>Verify</span>
                        </button>
                      </div>
                    </form>
                  </>
                )}

                {forgotStep === 'newpw' && (
                  <>
                    <h3 className="ac-modal-title">New <em>Password</em></h3>
                    <p style={{ fontSize:12, color:'#8A7E72', marginBottom:20, lineHeight:1.6 }}>
                      Choose a strong password with at least 8 characters.
                    </p>
                    {forgotError   && <div className="ac-error">{forgotError}</div>}
                    {forgotSuccess && <div className="ac-success">{forgotSuccess}</div>}
                    <form onSubmit={handleForgotReset}>
                      <Input label="New Password (min 8 characters)" type="password"
                        value={forgotNewPw} onChange={e => setForgotNewPw(e.target.value)}
                        icon={Shield} disabled={forgotLoading} />
                      <Input label="Confirm New Password" type="password"
                        value={forgotConfirmPw} onChange={e => setForgotConfirmPw(e.target.value)}
                        icon={Shield} disabled={forgotLoading} />
                      {forgotConfirmPw.length > 0 && (
                        <p style={{ fontSize:10, marginBottom:12, color: forgotNewPw === forgotConfirmPw ? '#2E7D32' : '#C0392B' }}>
                          {forgotNewPw === forgotConfirmPw ? '✓ Passwords match' : '✗ Passwords do not match'}
                        </p>
                      )}
                      <div className="ac-modal-footer">
                        <button type="button" className="ac-modal-cancel"
                          onClick={() => setForgotStep('otp')}>Back</button>
                        <button type="submit" className="ac-modal-save" disabled={forgotLoading}>
                          {forgotLoading && <Loader2 size={13} />}<span>Reset Password</span>
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </>
  );
};

export default AccountPage;